"use client";

import { toast } from "@/app/_providers/toast";
import { appUrls } from "@/constants";
import { Button } from "@components/core/Button";
import { Link } from "@components/core/Link";
import { AISpeechClassifierContext } from "@components/demos/AISpeechClassifier/AISpeechClassifier";
import { classifyAISpeech } from "@components/demos/AISpeechClassifier/api";
import Mic from "@components/demos/STS/assets/Mic";
import Prompts from "@components/demos/STS/assets/Prompts";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { useTranslations } from "next-intl";
import { useContext, useRef, useState } from "react";

const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB

export default function Overview() {
  const inputRef = useRef<HTMLInputElement>(null);
  const { setView, setProbability } = useContext(AISpeechClassifierContext);
  const [loading, setLoading] = useState(false);
  const t = useTranslations("ClientKeys.AISafetyClassifier");
  const tGlobal = useTranslations("ClientKeys.Globals");

  async function uploadHandler(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files?.length === 0) return;
    const file = e.target.files[0];
    if (file.size > MAX_UPLOAD_SIZE) {
      return toast.error("File too large", {
        description: "The file you are trying to upload is too large. Please upload a file that is less than 2MB.",
      });
    }
    setLoading(true);
    try {
      const probability = await classifyAISpeech(file);
      setProbability(probability.probability);
      setLoading(false);
      setView("result");
    } catch (error: any) {
      toast.error(error.message, {
        duration: 5000,
      });
      setLoading(false);
    }
  }

  return (
    <>
      <input
        accept='audio/aac, audio/x-aac, audio/x-aiff, audio/ogg, audio/mpeg, audio/mp3, audio/mpeg3, audio/x-mpeg-3, audio/opus, audio/wav, audio/x-wav, audio/webm, audio/flac, audio/x-flac, audio/mp4'
        className='hidden h-0 w-0'
        onChange={uploadHandler}
        ref={inputRef}
        type='file'
      />
      <div className='flex flex-1 gap-64 pt-72'>
        <div className='flex flex-1 flex-col items-center justify-center rounded-lg'>
          <div className='mb-20 flex items-center justify-center gap-12'>
            <Mic />
            <ChevronRight16 className='text-white' />
            <Prompts />
          </div>
          <p className='f-description-04 max-w-[300px] text-center'>
            {t("upload_sample_description")}
            <br />
            <br />
            <span className='text-light'>
              {t("report_concerns")}{" "}
              <Link className='underline' href={appUrls.supportForm} size={null}>
                {tGlobal("here")}
              </Link>
            </span>
          </p>
        </div>
      </div>
      <div className='flex justify-between gap-8 p-16 md:ms-auto'>
        <Button
          disabled={loading}
          icon={loading ? <Spinner20 /> : undefined}
          onClickCapture={() => inputRef.current?.click()}
        >
          {loading ? "Uploading..." : "Upload audio sample"}
        </Button>
      </div>
    </>
  );
}
